import ClickAwayListener from '@mui/material/ClickAwayListener';
import InputBase from '@mui/material/InputBase';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { colors } from '@virtus/components/theme';
import React, { CSSProperties, useState } from 'react';
import styled from 'styled-components';

export interface IProps {
  value: string;
  size: string;
  onChange: (event: any, data: any) => void;
  children: (a: any, s: string, closeHandler: () => void) => React.ReactChild;
  style?: CSSProperties;
  disabled?: boolean;
}

export interface IState {
  visible: boolean;
  search: string;
}

const noop = () => null;

const Dropdown: React.FC<IProps> = (props) => {
  const [visible, setVisible] = useState(false);
  const [search, setSearch] = useState('');

  const onClickAway = () => setVisible(false);

  const open = () => setVisible(true);

  const close = (e: any) => {
    const target = e.relatedTarget;
    if (target && target.attributes && target.attributes.role && target.attributes.role.value === 'showPortfolios') {
      return;
    }

    if (
      target &&
      target.attributes &&
      target.attributes.placeholder &&
      target.attributes.placeholder.value === 'Search...'
    ) {
      return;
    }

    setVisible(false);
  };

  const setToActive = (e: React.ChangeEvent<HTMLDivElement>, value: any) => {
    if (!value) {
      return;
    }
    props.onChange(e, value);
    setVisible(false);
  };

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearch(val);
  };

  const renderContents = () => {
    return (
      <Wrapper style={props.style}>
        <Container
          onClick={visible || props.disabled ? noop : open}
          size={props.size}
          disabled={props.disabled}
        >
          {visible ? (
            <div role="search" style={{ width: '100%' }}>
              <Input
                placeholder="Search..."
                value={search}
                onChange={onSearch}
                data-testid="dropdown-input"
              />
            </div>
          ) : (
            <Text size="md" data-testid="dropdown-value">
              {props.value}
            </Text>
          )}
          {visible ? (
            <ArrowUp data-testid="close-button" onClick={close} />
          ) : (
            <ArrowDown data-testid="open-button" disabled={props.disabled} />
          )}
        </Container>
        <Menu visible={visible} size={props.size} data-testid="dropdown-menu">
          {props.children(setToActive, search, () =>
            setVisible(!visible),
          )}
        </Menu>
      </Wrapper>
    );
  };

  return visible ? (
    <ClickAwayListener onClickAway={onClickAway}>{renderContents()}</ClickAwayListener>
  ) : (
    renderContents()
  );
};

const Wrapper: any = styled.div`
  margin-left: 10px;
  margin-right: 12px;
  background: rgba(255, 255, 255, 0.14);
  color: #333;

  &:focus {
    outline: none;
  }
`;

const containerHeights: any = {
  sm: '25px',
  md: '28px',
  lg: '28px',
  xl: '40px',
};
const fontSizes: any = { sm: '12px', md: '14px', lg: '18px', xl: '24px' };

const Container: any = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 500;
  padding: 5px 10px;
  border-radius: 3px;
  width: 100%;
  height: ${(props: any) => containerHeights[props.size]};
`;

const Menu: any = styled.div`
  position: absolute;
  display: ${(props: any) => (props.visible ? 'block' : 'none')};
  height: 260px;
  overflow-y: auto;
  margin-top: 5px;
  z-index: 501;
  background: ${(props: any) => (props.visible ? colors.accentDarkBlue : 'transparent')};
  border-radius: 3px;
  box-shadow: ${(props: any) => (props.visible ? '0px 2px 2px rgba(128, 134, 136, 0.35)' : 'none')};
`;

const Text: any = styled.p`
  color: ${colors.text};
  font-size: ${(props: any) => fontSizes[props.size]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  margin: 0px;
`;

const ArrowDown: any = styled(ArrowDropDown)<{ disabled?: boolean }>`
  color: ${props => (props.disabled ? colors.grey : colors.text)};
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
`;

const ArrowUp: any = styled(ArrowDropUp)`
  color: ${colors.text};
  cursor: pointer;
`;

const Input: any = styled(InputBase)`
  background: transparent;
  width: 100%;
  height: 45px;
  font-size: 14px;
  color: white;
  margin: 0;
  &:focus {
    outline: none;
    border: none !important;
  }
`;

export default Dropdown;
